import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';
import {
  InviteMemberRequestSchema,
  type InviteMemberRequest,
  MemberRoleSchema,
  type Member,
} from '@seo/types';
import { Page, PageHeader } from '@/components/ui/page';
import { SettingsTabs } from '@/components/settings-tabs';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { TextField, FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/spinner';
import { ErrorBanner } from '@/components/error-banner';
import { useAuthStore } from '@/lib/auth';
import { apiGet, apiPost } from '@/lib/api';
import { useToast, toastError } from '@/components/ui/toast';

interface MembersList {
  items: Member[];
}

function useMembers() {
  const tenantId = useAuthStore((s) => s.tenant?.id);
  return useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => apiGet<MembersList>(`/v1/tenants/${tenantId}/members`),
    enabled: !!tenantId,
  });
}

function useInviteMember() {
  const qc = useQueryClient();
  const tenantId = useAuthStore((s) => s.tenant?.id);
  return useMutation({
    mutationFn: (input: InviteMemberRequest) =>
      apiPost<Member>(`/v1/tenants/${tenantId}/members`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members', tenantId] });
    },
  });
}

export function MembersRoute(): JSX.Element {
  const members = useMembers();
  const invite = useInviteMember();
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<InviteMemberRequest>({
    resolver: zodResolver(InviteMemberRequestSchema),
    defaultValues: { email: '', role: 'strategist' },
  });
  const role = watch('role');

  const items = members.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        eyebrow="Settings"
        title="Members"
        description="Invite teammates and assign their workspace role."
      />
      <SettingsTabs />

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
              Invite a member
            </SectionTitle>
            <SectionDescription>
              They'll get an email with a link to join the workspace.
            </SectionDescription>
          </div>
        </SectionHeader>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={handleSubmit(async (values) => {
            try {
              await invite.mutateAsync(values);
              reset({ email: '', role: 'strategist' });
              toast({ title: 'Invitation sent' });
            } catch (e) {
              toastError(toast, e);
            }
          })}
        >
          <TextField
            className="flex-1 min-w-[220px]"
            label="Email"
            type="email"
            placeholder="teammate@company.com"
            error={errors.email?.message}
            {...register('email')}
          />
          <FormField label="Role" className="w-44">
            <Select
              value={role}
              onValueChange={(v) =>
                setValue('role', v as InviteMemberRequest['role'])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MemberRoleSchema.options.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <Button type="submit" disabled={invite.isPending}>
            {invite.isPending ? <Spinner className="mr-2" /> : null}
            Invite
          </Button>
        </form>
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Workspace members
              {items.length > 0 ? (
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  ({items.length})
                </span>
              ) : null}
            </SectionTitle>
          </div>
        </SectionHeader>
        {members.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : members.error ? (
          <ErrorBanner error={members.error} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Just you so far"
            description="Invite a teammate above to start collaborating."
            variant="inline"
          />
        ) : (
          <ul className="divide-y divide-border/70">
            {items.map((m) => (
              <li
                key={m.user_id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {m.display_name ?? m.email}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {m.email}
                  </div>
                </div>
                <Badge variant="secondary" className="capitalize">
                  {m.role}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </Page>
  );
}
